/**
 * Métricas reales del cliente — `/api/metrics`.
 *
 * Devuelve KPIs (conversaciones, % bot, % escalación, % SLA cumplido,
 * latencia p95) + serie diaria para el chart.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import type { ClientSlug } from "@/types";

export type MetricsPeriod = "7d" | "30d" | "90d";

export interface MetricsKpi {
  conversationsTotal: number;
  botAutoResolvedPct: number;
  escalationRatePct: number;
  slaCompliancePct: number;
  p95LatencyMs: number | null;
  messagesEscalated: number;
  messagesBotResolved: number;
}

export interface MetricsDayRow {
  day: string; // YYYY-MM-DD
  total: number;
  bot: number;
  escalated: number;
}

export interface MetricsResponse {
  clientSlug: ClientSlug;
  period: MetricsPeriod;
  slaTargetSec: number;
  kpi: MetricsKpi;
  dailySeries: MetricsDayRow[];
}

export function useMetrics(clientSlug: ClientSlug | undefined, period: MetricsPeriod) {
  return useQuery<MetricsResponse | null>({
    queryKey: ["metrics", clientSlug, period],
    enabled: !!clientSlug,
    refetchInterval: 60_000,
    queryFn: async () => {
      if (!clientSlug) return null;
      const qs = new URLSearchParams({ clientSlug, period });
      return apiGet<MetricsResponse>(`/metrics?${qs.toString()}`);
    },
  });
}
